import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useParams } from 'react-router';
import Avatar from '@atlaskit/avatar';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import AddIcon from '@atlaskit/icon/glyph/add';
import {
  CustomSearch,
  DynamicResultTable,
  errorNotificationBuilder,
  NotificationTypes,
  OpenSidebarButton,
  useNotification,
  ContactType,
  InfoPanel,
  ContactsTooltipType,
  getTooltipContent,
} from '@css-xp/base';
import { emptyStateProp, TableHeading } from '@css-xp/ppc-common';
import { useFetchEccFromContactService, ContactType as ContactResourceType } from '@css-xp/contact-resources';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { ContactInfo, BillingAdmin } from '@css-xp/graphtypes/src/generated-types-new';
import {
  emptyStatePropForCustomerContactsTab,
  emptyStatePropForIULContactsTab,
  emptyStatePropForWrongSearch,
  emptyStatePropForBillingProfilesBillingAdminsTab,
} from '@css-xp/ppc-common/src/state-props';
import './customer-contacts-tab.scss';
import { CustomerContactActions } from './customer-contact-actions';

import { BillingAdminActions } from './billing-admin-actions';

interface CustomerContactsTabProps {
  entitlementId?: string;
  invoiceGroupId: string;
  label?: string;
  version: number;
  customerContactsSidebarV2?: string;
  showTableActionColumn: boolean;
  customerContactType?: ContactResourceType;
  transactionAccountBillingAdmins?: BillingAdmin[];
  transactionAccountBillingAdminsLoading?: boolean;
  customerName?: string;
  shouldDisableAddEditActions?: boolean;
  shouldRenderForInternalUse?: boolean;
  isCustomerContactsEditable?: boolean;
  validPartnerDomains?: string[];
}

interface FormData {
  searchTerm: string;
}

interface UserDetails {
  accountId: string;
  picture: string;
  name: string;
  email?: string;
  isPrimary?: boolean;
}

const DEFAULT_ROWS_PER_PAGE = 8;

const getDataForCurrentPage = (currentPage: number, pageSize: number, data: Array<UserDetails>) => {
  if (!data || data.length === 0) return [];
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return data?.slice(start, end);
};

const errorFetchingContactsOptions = (isBillingAdminType: boolean) =>
  errorNotificationBuilder()
    .isVersion(NotificationTypes.FLAG)
    .withId('fetch-user-failure')
    .withHeader(
      isBillingAdminType
        ? 'Error while fetching billing admin details'
        : 'Error while fetching customer contact details'
    )
    .withDismissAfter(5000)
    .build();

export const CustomerContactsTab = ({
  entitlementId,
  invoiceGroupId,
  label,
  version,
  customerContactsSidebarV2,
  showTableActionColumn,
  customerContactType = ContactResourceType.CUSTOMER_CONTACT,
  transactionAccountBillingAdmins,
  transactionAccountBillingAdminsLoading,
  customerName = '',
  shouldDisableAddEditActions,
  shouldRenderForInternalUse,
  isCustomerContactsEditable,
  validPartnerDomains,
}: CustomerContactsTabProps) => {
  const { txaId } = useParams<{ txaId: string }>();
  const [tableViewData, setTableViewData] = useState<{
    data: Array<UserDetails>;
    currentPage: number;
    rowsPerPage: number;
  }>({
    data: [],
    currentPage: 1,
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
  });
  const [eccDetails, setEccDetails] = useState([]);
  const [isEccLoading, setIsECCLoading] = useState(false);
  const [eccCount, setEccCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [primaryContactDetails, setPrimaryContactDetails] = useState<ContactInfo>({});

  const isBillingAdminType = customerContactType === ContactResourceType.BILLING_CONTACT;
  const isCustomerContactType = customerContactType === ContactResourceType.CUSTOMER_CONTACT;
  const isVersion2Enabled = version === 2;

  const isPrimaryCustomerContactPresent = eccDetails.some((ecc: ContactInfo) => ecc.isPrimary);

  const { sendErrorNotification, notificationSent, setNotificationSent } = useNotification();

  const { getECCDetails } = useFetchEccFromContactService(customerContactType);

  const sortContactsByEmail = (contact1: ContactInfo, contact2: ContactInfo) => {
    const contact1Email = contact1?.email ?? '';
    const contact2Email = contact2?.email ?? '';
    return contact1Email.localeCompare(contact2Email);
  };

  const fetchECCDetailsFromContactService = useCallback(async () => {
    setIsECCLoading(true);
    try {
      const data = await getECCDetails(invoiceGroupId as string, txaId);
      const sortedData = data?.sort(sortContactsByEmail);
      setEccDetails(sortedData);
      setEccCount(data.length);
    } catch (error) {
      if (!notificationSent) {
        sendErrorNotification(errorFetchingContactsOptions(isBillingAdminType));
        setNotificationSent(true);
      }
    } finally {
      setIsECCLoading(false);
    }
  }, [invoiceGroupId, isBillingAdminType, txaId]);

  useEffect(() => {
    const primaryContact = eccDetails?.find((ecc: ContactInfo) => ecc?.isPrimary);
    setPrimaryContactDetails(primaryContact ?? {});
  }, [eccDetails]);

  useEffect(() => {
    fetchECCDetailsFromContactService();
  }, [invoiceGroupId, fetchECCDetailsFromContactService]);

  const filteredEccDetailsFromSearchedInput = (eccDetails: ContactInfo[], searchValue: string) => {
    const escapedvalue = searchValue.trim().replace(/[+]/g, '\\$&');
    const regexPattern = `\\s*${escapedvalue}\\s*`;

    return eccDetails.filter((eccDetail) => {
      const hasNameMatched = new RegExp(regexPattern, 'ig').test(eccDetail?.name as string);
      const hasEmailMatched = new RegExp(regexPattern, 'ig').test(eccDetail?.email as string);
      return hasNameMatched || hasEmailMatched;
    });
  };

  const endCustomerContacts = useMemo(() => {
    const filteredEccDetails = filteredEccDetailsFromSearchedInput(eccDetails, searchInput);
    return filteredEccDetails;
  }, [eccDetails, searchInput]);

  useEffect(() => {
    setTableViewData({
      ...tableViewData,
      currentPage: 1,
      data: getDataForCurrentPage(1, tableViewData.rowsPerPage, endCustomerContacts as any),
    });
  }, [endCustomerContacts]);

  const handlePageChange = useCallback(
    (pageNo: number) => {
      setTableViewData({
        ...tableViewData,
        currentPage: pageNo,
        data: getDataForCurrentPage(pageNo, tableViewData.rowsPerPage, endCustomerContacts as any),
      });
    },
    [tableViewData, endCustomerContacts]
  );

  const handleRowSizeChange = useCallback(
    (rowSize: number) => {
      setTableViewData({
        currentPage: 1,
        rowsPerPage: rowSize,
        data: getDataForCurrentPage(1, rowSize, endCustomerContacts as any),
      });
    },
    [endCustomerContacts]
  );

  const renderTableHead = useCallback(() => {
    if (eccCount === 0) {
      return { cells: [] };
    }
    return {
      cells: [
        {
          content: `${label}`,
        },
        ...(showTableActionColumn ? [{ content: 'Actions' }] : []),
      ],
    };
  }, [eccCount, label, showTableActionColumn]);

  const getPayloadForTheSidebar = () => {
    return {
      transactionAccountId: txaId,
      invoiceGroupId: invoiceGroupId,
      refetch: fetchECCDetailsFromContactService,
      version: version,
      transactionAccountBillingAdmins,
      shouldRenderForInternalUse,
      isCustomerContactsEditable,
      primaryContactDetails: primaryContactDetails,
      validPartnerDomains: validPartnerDomains,
    };
  };

  const onSearch = useCallback(async (formData: FormData) => {
    const searchInputVal = formData?.searchTerm;
    setSearchInput(searchInputVal);
  }, []);

  const renderSearch = () => {
    if (isEccLoading && eccCount === 0) {
      return null;
    }

    return (
      <div className="customer-contacts-search-bar-v2">
        <CustomSearch
          placeholder="Search"
          onSearch={onSearch}
          isClearable={true}
          helperMessage="Enter name or email address"
        />
      </div>
    );
  };

  const renderActionsOnTop = useCallback(() => {
    let sidebarButtonText = `Add ${ContactType.CUSTOMER_CONTACT}`;

    if (isBillingAdminType) {
      sidebarButtonText = 'Add billing admin';
    } else if (shouldRenderForInternalUse) {
      sidebarButtonText = 'Add contact';
    }

    return [
      () => (
        <AdvocateAccessWall
          accessType={ADVOCATE_ACCESS_TYPE.CREATE}
          resource={ADVOCATE_RESOURCE_TYPE.END_CUSTOMER_CONTACT}
        >
          <OpenSidebarButton
            sidebar={customerContactsSidebarV2 as string}
            payload={getPayloadForTheSidebar()}
            sidebarOptions={{
              sidebarWidth: isVersion2Enabled ? undefined : '600px',
              isOverlay: true,
              sidebarTitle: sidebarButtonText,
            }}
            iconBefore={<AddIcon label="Add icon" size="small" />}
            appearance="default"
            testId="add-customer-contact"
            className="add-customer-contact-button"
            isDisabled={
              isEccLoading ||
              (isVersion2Enabled && transactionAccountBillingAdminsLoading) ||
              shouldDisableAddEditActions
            }
          >
            {sidebarButtonText}
          </OpenSidebarButton>
        </AdvocateAccessWall>
      ),
    ];
  }, [
    entitlementId,
    txaId,
    isEccLoading,
    invoiceGroupId,
    customerContactType,
    isBillingAdminType,
    isVersion2Enabled,
    transactionAccountBillingAdminsLoading,
  ]);

  const getRowAction = useCallback(
    (user: UserDetails) => {
      if (!showTableActionColumn) {
        return [{}];
      }
      if (isBillingAdminType) {
        return [
          {
            key: `${user.accountId}-actions`,
            content: (
              <BillingAdminActions
                contact={user.accountId}
                invoiceGroupId={invoiceGroupId}
                refetch={fetchECCDetailsFromContactService}
              />
            ),
          },
        ];
      } else if (isCustomerContactType) {
        return [
          {
            key: `${user.accountId}-actions`,
            content: (
              <CustomerContactActions
                user={user}
                customerName={customerName}
                invoiceGroupId={invoiceGroupId}
                isPrimaryCustomerContactPresent={isPrimaryCustomerContactPresent}
                refetch={fetchECCDetailsFromContactService}
                shouldDisableAllActions={shouldDisableAddEditActions}
                shouldRenderForInternalUse={shouldRenderForInternalUse}
                isCustomerContactsEditable={isCustomerContactsEditable}
                primaryContactDetails={primaryContactDetails}
                validPartnerDomains={validPartnerDomains}
              />
            ),
          },
        ];
      }
      return [{}];
    },
    [
      showTableActionColumn,
      isBillingAdminType,
      isCustomerContactType,
      customerName,
      invoiceGroupId,
      isPrimaryCustomerContactPresent,
      fetchECCDetailsFromContactService,
    ]
  );

  const renderRows = useCallback(() => {
    return tableViewData.data.map((user: UserDetails, idx: number) => {
      return {
        key: user.accountId,
        cells: [
          {
            key: `${user.accountId}-details`,
            content: (
              <div className="user-details-section">
                <Avatar src={user.picture} size="large" />
                <div className="user-name-email">
                  <Flex alignItems="center">
                    <div className={classNames('user-name', { 'user-name-v2': isVersion2Enabled })}>{user.name}</div>
                    {isCustomerContactType && user.isPrimary && (
                      <Box paddingInlineStart="space.100">
                        <Flex>
                          <Lozenge appearance={isVersion2Enabled ? 'new' : 'default'}>
                            {shouldRenderForInternalUse ? 'Key contact' : 'Primary'}
                          </Lozenge>
                        </Flex>
                      </Box>
                    )}
                  </Flex>
                  <div
                    className={classNames('user-email', {
                      'user-email-v2': isVersion2Enabled,
                    })}
                    data-test-id={`user-email-${idx}`}
                  >
                    {user.email || ''}
                  </div>
                </div>
              </div>
            ),
          },
          ...getRowAction(user),
        ],
      };
    });
  }, [isCustomerContactType, isVersion2Enabled, tableViewData.data]);

  const getEmptyStatePropV2 = () => {
    const [AddCustomerContactButton] = renderActionsOnTop();
    const emptyState = isBillingAdminType
      ? emptyStatePropForBillingProfilesBillingAdminsTab
      : shouldRenderForInternalUse
      ? emptyStatePropForIULContactsTab
      : emptyStatePropForCustomerContactsTab;
    return eccCount ? emptyStatePropForWrongSearch : { ...emptyState, primaryAction: <AddCustomerContactButton /> };
  };

  const emptyStateProps = isVersion2Enabled ? getEmptyStatePropV2() : emptyStateProp;

  return (
    <div className="customer-contacts-container">
      <div
        className={classNames('customer-contacts-table', {
          'customer-contacts-table-v2': isVersion2Enabled,
        })}
      >
        <DynamicResultTable
          head={isVersion2Enabled && endCustomerContacts.length ? renderTableHead() : undefined}
          beforeTableHead={
            !isEccLoading && isCustomerContactType && !isPrimaryCustomerContactPresent ? (
              <Box xcss={xcss({ width: '100%' })} paddingBlockEnd="space.100">
                {shouldRenderForInternalUse ? (
                  <InfoPanel
                    title={'Add a key contact to the billing profile'}
                    message={getTooltipContent(ContactsTooltipType.KEY_CONTACT)}
                    type="discovery"
                  />
                ) : (
                  <InfoPanel
                    title={'Add a primary contact to the billing profile'}
                    message={
                      'A primary contact is your customer’s main point of contact who liaises between the organization and you for quotes, orders and billing.'
                    }
                    type="discovery"
                  />
                )}
              </Box>
            ) : null
          }
          isLoading={isEccLoading}
          testId="customer-contact"
          beforeTable={isVersion2Enabled ? null : <TableHeading>{label}</TableHeading>}
          className={classNames('customer-contacts-table', {
            'customer-contacts-table-action-column': showTableActionColumn,
          })}
          rows={renderRows()}
          emptyStateProps={emptyStateProps}
          totalCount={endCustomerContacts.length}
          currentPage={tableViewData.currentPage}
          rowsPerPage={tableViewData.rowsPerPage}
          alternateRowColor={false}
          onPageChange={handlePageChange}
          isPaginationEnabled={endCustomerContacts.length > DEFAULT_ROWS_PER_PAGE}
          onRowChange={handleRowSizeChange}
          actions={isEccLoading || (isVersion2Enabled && !eccCount) ? [] : renderActionsOnTop()}
          showFilters
        >
          {renderSearch()}
        </DynamicResultTable>
      </div>
    </div>
  );
};
